/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2021 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";

import UserManagementDetails from "./UserManagementDetails";
import Notification from "../common/Notification";
import RightDrawer from "../../containers/RightDrawer";
import UserManagementRow from "./UserManagementRow";
import StyledNoResults from "../common/StyledNoResults";
import StyledOverlay from "../common/StyledOverlay";
import { fetchUsers } from "../../slices/userManagementSlice";
import { updateUserManagementFilters } from "../../slices/userManagementFiltersSlice";
import types from "../../helpers/types";

const StyledTableContainer = styled(TableContainer)`
  flex: 1 0 auto;
  height: 0px;
`;

const StyledTableRow = styled(TableRow)`
  .sticky {
    position: sticky !important;
    top: 0;
    z-index: 90;
  }
`;

const StyledTablePagination = styled(TablePagination)`
  flex: 0 0 52px;
  border-top: 2px solid ${(props) => props.theme.palette.background.lightGrey} !important;
  border-bottom: 2px solid ${(props) => props.theme.palette.grey[300]} !important;
  overflow: hidden !important;
`;

const USER_MANAGEMENT_LIMIT = window.env.USER_MANAGEMENT_LIMIT;

const UserManagementTable = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const history = useHistory();

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [
    showUserUpdateSuccessNotification,
    setShowUserUpdateSuccessNotification,
  ] = useState(false);
  const [
    showInviteResentSuccessNotification,
    setShowInviteResentSuccessNotification,
  ] = useState(false);
  const [unsavedModalOpen, setUnsavedModalOpen] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const users = useSelector((state) => state.userManagementUsers.users);
  const isFetching = useSelector(
    (state) => state.userManagementUsers.isFetching
  );
  const totalCount = useSelector(
    (state) => state.userManagementUsers.totalCount
  );
  const currentPage = useSelector(
    (state) => state.userManagementFilters.currentPage
  );

  useEffect(() => {
    dispatch(fetchUsers(currentPage));
  }, []);

  const handlePageChange = (event, newPage) => {
    dispatch(
      updateUserManagementFilters({
        filters: { currentPage: newPage },
        history,
      })
    );
  };

  const rowsPerPage = parseInt(USER_MANAGEMENT_LIMIT, 10);

  return (
    <>
      {isFetching && <StyledOverlay theme={theme} />}
      {users.length > 0 ? (
        <>
          {selectedUserId && (
            <RightDrawer width={550}>
              <UserManagementDetails
                selectedUserId={selectedUserId}
                setSelectedUserId={setSelectedUserId}
                setShowUserUpdateSuccessNotification={
                  setShowUserUpdateSuccessNotification
                }
                setShowInviteResentSuccessNotification={
                  setShowInviteResentSuccessNotification
                }
                setUnsavedChanges={setUnsavedChanges}
                unsavedChanges={unsavedChanges}
                setUnsavedModalOpen={setUnsavedModalOpen}
                unsavedModalOpen={unsavedModalOpen}
              />
            </RightDrawer>
          )}
          <StyledTableContainer>
            <Table size="small">
              <TableHead>
                <StyledTableRow>
                  <TableCell theme={theme} align="left" key="email">
                    Email
                  </TableCell>
                  <TableCell theme={theme} align="left" key="firstName">
                    First Name
                  </TableCell>
                  <TableCell theme={theme} align="left" key="lastName">
                    Last Name
                  </TableCell>
                  <TableCell theme={theme} align="left" key="userType">
                    User Type
                  </TableCell>
                  <TableCell theme={theme} align="left" key="lastActive">
                    Last Active
                  </TableCell>
                  <TableCell theme={theme} align="left" key="invited">
                    Invited
                  </TableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {users.map((row) => {
                  return (
                    <UserManagementRow
                      key={row.usersId}
                      row={row}
                      setSelectedUserId={setSelectedUserId}
                      setUnsavedModalOpen={setUnsavedModalOpen}
                      unsavedChanges={unsavedChanges}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </StyledTableContainer>
          {!isFetching && (
            <StyledTablePagination
              component="div"
              theme={theme}
              count={totalCount}
              rowsPerPageOptions={[rowsPerPage]}
              rowsPerPage={rowsPerPage}
              page={currentPage}
              onChangePage={handlePageChange}
            />
          )}{" "}
        </>
      ) : (
        <StyledNoResults theme={theme}>
          {!isFetching && "No Results"}
        </StyledNoResults>
      )}
      <Notification
        message="User updated."
        open={showUserUpdateSuccessNotification}
        setOpen={setShowUserUpdateSuccessNotification}
      />
      <Notification
        message="Invitation resent."
        open={showInviteResentSuccessNotification}
        setOpen={setShowInviteResentSuccessNotification}
      />
    </>
  );
};

export default UserManagementTable;
